import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useApi } from '../auth/ApiProvider';
import { useSession } from '../auth/SessionProvider';
import { useI18n } from '../i18n/I18nProvider';
import {
  PushRefusedError, currentSubscription, disablePush, enablePush, pushSupport, refreshPush,
} from '../notifications/webPush';
import { Button, Card, Notice, Row, Stack } from './primitives';
import { ErrorState } from './states';

const SUBSCRIPTION_KEY = ['push', 'subscription'] as const;

/**
 * Turning push on for this phone (Q68), above the notification preferences in the guardian app.
 *
 * Offered only where the deployment has push keys and the browser can subscribe; an iPhone outside a home-screen
 * web app is told how to add it, since Safari pushes to nothing else. The push itself says only what kind of thing
 * happened (FR-NOT-08), and the card says so, so nobody expects a name on the lock screen.
 */
export function PushCard() {
  const api = useApi();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const support = pushSupport();

  const config = useQuery({
    queryKey: ['push', 'config'],
    queryFn: () => api.notifications.pushConfig(),
    staleTime: 60 * 60_000,
  });

  const subscription = useQuery({
    queryKey: SUBSCRIPTION_KEY,
    queryFn: async () => (await currentSubscription())?.endpoint ?? null,
    enabled: support === 'supported' && config.data?.enabled === true,
  });

  const turnOn = useMutation({
    mutationFn: () => enablePush(api, config.data),
    onSettled: () => queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY }),
  });

  const turnOff = useMutation({
    mutationFn: () => disablePush(api),
    onSettled: () => queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY }),
  });

  if (!config.data?.enabled || support === 'unsupported') return null;

  if (support === 'ios-install') {
    return <Notice tone="info" title={t('push.ios.title')}>{t('push.ios.body')}</Notice>;
  }

  const denied = Notification.permission === 'denied'
    || (turnOn.error instanceof PushRefusedError && turnOn.error.reason === 'denied');
  const on = !!subscription.data && !denied;

  return (
    <Card title={t('push.title')} hint={t('push.hint')}>
      <Stack>
        {denied && <Notice tone="warning">{t('push.denied')}</Notice>}
        {turnOn.isError && !(turnOn.error instanceof PushRefusedError) && <ErrorState error={turnOn.error} />}
        {turnOff.isError && <ErrorState error={turnOff.error} />}
        {on && <p>{t('push.on')}</p>}
        <Row>
          {on ? (
            <Button
              variant="secondary"
              busy={turnOff.isPending}
              busyLabel={t('action.saving')}
              onClick={() => turnOff.mutate()}
            >
              {t('push.turnOff')}
            </Button>
          ) : (
            <Button
              busy={turnOn.isPending}
              busyLabel={t('action.saving')}
              disabled={denied || subscription.isPending}
              onClick={() => turnOn.mutate()}
            >
              {t('push.turnOn')}
            </Button>
          )}
        </Row>
      </Stack>
    </Card>
  );
}

/**
 * Sends this browser's subscription again each time the guardian app opens signed in, so the API's copy follows a
 * browser that renewed it. Silent: a failure here changes nothing the person can see.
 */
export function usePushRefresh(): void {
  const api = useApi();
  const { status, session } = useSession();
  const membershipId = session?.membership.membershipId;

  useEffect(() => {
    if (status !== 'authenticated' || membershipId === undefined) return;
    void refreshPush(api).catch(() => undefined);
  }, [api, status, membershipId]);
}
